// 页 1：AI 候选看板
// 10 状态分桶看板 + 列表视图切换 + 筛选

import React, { useState, useMemo } from 'react';
import { Card, Row, Col, Tag, Image, Select, Radio, Input, Empty, Badge, Statistic, Tooltip } from 'antd';
import { SearchOutlined, AppstoreOutlined, BarsOutlined } from '@ant-design/icons';
import {
  aiCandidates, STATUS_LABELS, STATUS_COLORS, SITE_NAMES,
} from '../data';
import { AiCandidate, LifecycleStatus, SiteId } from '../types';

const BOARD_STATUSES: LifecycleStatus[] = [
  'DISCOVERED', 'SELECTED', 'IMAGE_GENERATING', 'IMAGE_REVIEW',
  'LISTING_DRAFT', 'PRICED', 'SHOPIFY_DRAFT', 'PUBLISHED', 'MEASURING', 'LEARNED'
];

interface Props {
  onOpenDetail: (candidateId: string) => void;
}

const CandidateBoard: React.FC<Props> = ({ onOpenDetail }) => {
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [filterStyle, setFilterStyle] = useState<string>('all');
  const [filterSite, setFilterSite] = useState<string>('all');
  const [searchText, setSearchText] = useState('');

  const filtered = useMemo(() => {
    return aiCandidates.filter(c => {
      if (filterStyle !== 'all' && c.style_id !== filterStyle) return false;
      if (filterSite !== 'all' && String(c.site_id) !== filterSite) return false;
      if (searchText && !c.title.includes(searchText)) return false;
      return true;
    });
  }, [filterStyle, filterSite, searchText]);

  const byStatus = useMemo(() => {
    const map: Record<string, AiCandidate[]> = {};
    BOARD_STATUSES.forEach(s => (map[s] = []));
    filtered.forEach(c => {
      if (map[c.status]) map[c.status].push(c);
    });
    return map;
  }, [filtered]);

  // 统计
  const stats = {
    total: filtered.length,
    inReview: byStatus.IMAGE_REVIEW.length + byStatus.LISTING_DRAFT.length + byStatus.PRICED.length,
    published: byStatus.PUBLISHED.length + byStatus.MEASURING.length + byStatus.LEARNED.length,
    rejected: aiCandidates.filter(c => c.status === 'REJECTED').length,
  };

  return (
    <div style={{ padding: '16px 24px' }}>
      {/* KPI 头条 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}><Card><Statistic title="候选总数" value={stats.total} /></Card></Col>
        <Col span={6}><Card><Statistic title="待运营 review" value={stats.inReview} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="已上架" value={stats.published} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={6}><Card><Statistic title="已拒绝" value={stats.rejected} valueStyle={{ color: '#cf1322' }} /></Card></Col>
      </Row>

      {/* 工具栏 */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={8} align="middle">
          <Col><span>视图:</span></Col>
          <Col>
            <Radio.Group value={viewMode} onChange={e => setViewMode(e.target.value)} buttonStyle="solid" size="small">
              <Radio.Button value="kanban"><AppstoreOutlined /> 看板</Radio.Button>
              <Radio.Button value="list"><BarsOutlined /> 列表</Radio.Button>
            </Radio.Group>
          </Col>
          <Col style={{ marginLeft: 24 }}>风格:</Col>
          <Col>
            <Select value={filterStyle} onChange={setFilterStyle} style={{ width: 120 }} size="small">
              <Select.Option value="all">全部</Select.Option>
              {['哥特', '暗黑', '森系', '古典', '极简', '复古'].map(s => (
                <Select.Option key={s} value={s}>{s}</Select.Option>
              ))}
            </Select>
          </Col>
          <Col style={{ marginLeft: 12 }}>站点:</Col>
          <Col>
            <Select value={filterSite} onChange={setFilterSite} style={{ width: 100 }} size="small">
              <Select.Option value="all">全部</Select.Option>
              <Select.Option value="0">FG</Select.Option>
              <Select.Option value="1">ST</Select.Option>
              <Select.Option value="2">BF</Select.Option>
            </Select>
          </Col>
          <Col style={{ marginLeft: 12, flex: 1 }}>
            <Input prefix={<SearchOutlined />} placeholder="搜索候选标题" value={searchText}
              onChange={e => setSearchText(e.target.value)} size="small" />
          </Col>
        </Row>
      </Card>

      {/* 看板视图 */}
      {viewMode === 'kanban' && (
        <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16 }}>
          {BOARD_STATUSES.map(status => (
            <div key={status} style={{ minWidth: 260, flex: '0 0 260px' }}>
              <Card
                size="small"
                title={
                  <span style={{ color: STATUS_COLORS[status] }}>
                    {STATUS_LABELS[status]} <Badge count={byStatus[status].length} style={{ backgroundColor: STATUS_COLORS[status] }} />
                  </span>
                }
                style={{ borderTop: `3px solid ${STATUS_COLORS[status]}` }}
              >
                <div style={{ maxHeight: 'calc(100vh - 380px)', overflowY: 'auto' }}>
                  {byStatus[status].length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="" style={{ padding: 16 }} />
                  ) : (
                    byStatus[status].map(c => (
                      <Card
                        key={c.candidate_id}
                        size="small"
                        hoverable
                        style={{ marginBottom: 8 }}
                        bodyStyle={{ padding: 8, cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', gap: 8 }} onClick={() => onOpenDetail(c.candidate_id)}>
                          <Image src={c.thumbnail_url} width={60} height={60}
                            style={{ objectFit: 'cover', borderRadius: 4 }} preview={false} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {c.title}
                            </div>
                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                              {c.candidate_id}
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <Tag color="blue">{c.style_id}</Tag>
                              <Tag>{SITE_NAMES[c.site_id]}</Tag>
                            </div>
                            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
                              {c.owner_name} · {c.created_by_ai_model}
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* 列表视图 */}
      {viewMode === 'list' && (
        <Card size="small">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0', textAlign: 'left' }}>
                <th style={{ padding: 8 }}></th>
                <th style={{ padding: 8 }}>候选 ID</th>
                <th style={{ padding: 8 }}>标题</th>
                <th style={{ padding: 8 }}>状态</th>
                <th style={{ padding: 8 }}>风格</th>
                <th style={{ padding: 8 }}>站点</th>
                <th style={{ padding: 8 }}>负责人</th>
                <th style={{ padding: 8 }}>AI 模型</th>
                <th style={{ padding: 8 }}>批次</th>
                <th style={{ padding: 8 }}>创建</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.candidate_id} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}
                  onClick={() => onOpenDetail(c.candidate_id)}>
                  <td style={{ padding: 8 }}>
                    <Image src={c.thumbnail_url} width={40} height={40} preview={false}
                      style={{ objectFit: 'cover', borderRadius: 4 }} />
                  </td>
                  <td style={{ padding: 8, fontSize: 12, fontFamily: 'monospace' }}>{c.candidate_id}</td>
                  <td style={{ padding: 8 }}>{c.title}</td>
                  <td style={{ padding: 8 }}><Tag color={STATUS_COLORS[c.status]}>{STATUS_LABELS[c.status]}</Tag></td>
                  <td style={{ padding: 8 }}><Tag color="blue">{c.style_id}</Tag></td>
                  <td style={{ padding: 8 }}>{SITE_NAMES[c.site_id]}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>{c.owner_name}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>{c.created_by_ai_model}</td>
                  <td style={{ padding: 8, fontSize: 12 }}>{c.batch_no}</td>
                  <td style={{ padding: 8, fontSize: 11, color: '#8c8c8c' }}>
                    {new Date(c.created_at).toLocaleDateString('zh-CN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default CandidateBoard;
